import type { INavigator, NavigateResponse, StateStore } from 'oidc-client-ts';
import { STARTUP_TIMEOUT_MS, StartupTimeoutError } from './startup';

type EndReason = 'logout' | 'replaced' | 'cleanup' | 'cancelled' | 'timeout' | 'terminal';

export function createAuthLifetime(storage: Storage, userKey: string) {
  const controller = new AbortController();
  let reason: EndReason | null = null;
  const listeners = new Set<() => void>();
  const check = () => controller.signal.throwIfAborted();
  // OIDC writes the user before resolving signinSilent/callback. Guard the
  // actual storage boundary so stale completions cannot resurrect on reload
  // or remove a newer manager's user, even after its listeners are detached.
  const store: StateStore = {
    async get(key) { check(); return storage.getItem(`oidc.${key}`); },
    async set(key, value) { check(); storage.setItem(`oidc.${key}`, value); },
    async remove(key) {
      check();
      const value = storage.getItem(`oidc.${key}`);
      storage.removeItem(`oidc.${key}`);
      return value;
    },
    async getAllKeys() {
      check();
      return Array.from({ length: storage.length }, (_, index) => storage.key(index))
        .filter((key): key is string => key !== null && key.startsWith('oidc.'))
        .map(key => key.slice(5));
    }
  };
  const end = (nextReason: EndReason, clearUser = false) => {
    if (reason) return;
    reason = nextReason;
    if (clearUser) {
      try { storage.removeItem(`oidc.${userKey}`); } catch { /* the invalid lifetime still denies every write */ }
    }
    controller.abort(nextReason === 'timeout' ? new StartupTimeoutError() : new DOMException('Authentication operation cancelled', 'AbortError'));
    for (const listener of listeners) listener();
    listeners.clear();
  };
  return {
    store,
    active: () => reason === null,
    reason: () => reason,
    end,
    onEnd(listener: () => void) {
      if (reason) listener(); else listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    async run<T>(load: () => Promise<T>): Promise<T> {
      check();
      return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => end('timeout', true), STARTUP_TIMEOUT_MS);
        const cleanup = () => {
          clearTimeout(timer);
          controller.signal.removeEventListener('abort', abort);
        };
        const abort = () => { cleanup(); reject(controller.signal.reason); };
        controller.signal.addEventListener('abort', abort, { once: true });
        void Promise.resolve().then(() => { check(); return load(); }).then(value => {
          check();
          resolve(value);
        }, reject).catch(reject).finally(cleanup);
      });
    }
  };
}

export type AuthLifetime = ReturnType<typeof createAuthLifetime>;

// signinRedirect can await discovery before invoking its navigator. Checking
// only before calling signinRedirect cannot fence that later navigation.
export function authRedirectNavigator(lifetime: AuthLifetime, browser: Window = window): INavigator {
  return {
    async prepare(params) {
      const options = params as { redirectMethod?: 'assign' | 'replace'; redirectTarget?: 'self' | 'top' };
      const target = options.redirectTarget === 'top' ? browser.top ?? browser : browser;
      let closed = false;
      let cancel: (() => void) | undefined;
      return {
        navigate: ({ url }) => new Promise<NavigateResponse>((resolve, reject) => {
          if (!lifetime.active() || closed) {
            reject(new DOMException('Authentication navigation cancelled', 'AbortError'));
            return;
          }
          let detach = () => undefined as void;
          const cleanup = () => { browser.removeEventListener('pageshow', complete); detach(); };
          const complete = () => { cleanup(); resolve({ url: browser.location.href }); };
          cancel = () => { cleanup(); reject(new DOMException('Authentication navigation cancelled', 'AbortError')); };
          browser.addEventListener('pageshow', complete, { once: true });
          detach = lifetime.onEnd(cancel);
          if (!lifetime.active() || closed) { cancel(); return; }
          try { target.location[options.redirectMethod ?? 'assign'](url); }
          catch (error) { cleanup(); reject(error); }
        }),
        close: () => { closed = true; cancel?.(); }
      };
    },
    async callback() {}
  };
}

// Requests share a single renewal, but keep independent cancellation. The
// manager is invalidated only when no live consumer still needs the result.
export function sharedAuthOperation<T>(lifetime: AuthLifetime, load: () => Promise<T>, accept: (value: T) => void) {
  const consumers = new Set<object>();
  let operation: Promise<T> | null = null;
  let settled = false;
  const join = (signal?: AbortSignal | null): Promise<T> => {
    signal?.throwIfAborted();
    const consumer = {};
    consumers.add(consumer);
    operation ??= lifetime.run(load).then(value => {
      if (!lifetime.active() || consumers.size === 0) throw new DOMException('Authentication operation cancelled', 'AbortError');
      settled = true;
      accept(value);
      return value;
    }).finally(() => { settled = true; });
    return new Promise<T>((resolve, reject) => {
      const cancel = () => {
        consumers.delete(consumer);
        reject(signal?.reason ?? new DOMException('Request cancelled', 'AbortError'));
        if (!settled && consumers.size === 0) lifetime.end('cancelled', true);
      };
      signal?.addEventListener('abort', cancel, { once: true });
      void operation!.then(value => {
        if (signal?.aborted) cancel(); else resolve(value);
      }, reject).finally(() => {
        consumers.delete(consumer);
        signal?.removeEventListener('abort', cancel);
      });
    });
  };
  return { join, settled: () => settled };
}
